import { useLocale, useTranslator } from "gemi/client";
import { useEffect, useState } from "react";
import { font } from "./_shared";

// All supported locales — the v2 dictionaries carry all seven, so the picker offers them all.
// The device default (TR → TR, else EN) is decided by the framework locale detection; this switcher
// only lets a visitor pick another language manually (an explicit pick is remembered via the cookie).
const LOCALES = [
  { value: "en-US", short: "EN", name: "English" },
  { value: "tr-TR", short: "TR", name: "Türkçe" },
  { value: "de-DE", short: "DE", name: "Deutsch" },
  { value: "it-IT", short: "IT", name: "Italiano" },
  { value: "fr-FR", short: "FR", name: "Français" },
  { value: "es-ES", short: "ES", name: "Español" },
  { value: "ru-RU", short: "RU", name: "Русский" },
] as const;

export function LanguageToggleV2({ onSelect }: { onSelect?: () => void } = {}) {
  const t = useTranslator("SiteNavV2");
  const [locale, setLocale] = useLocale();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const current = LOCALES.find((l) => l.value === locale) ?? LOCALES[0];

  return (
    <div className="relative flex-none" style={font.body}>
      <button
        type="button"
        aria-label={t("language")}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 rounded-full border border-[rgba(25,24,24,0.14)] bg-[#F3F1EC] px-3 py-1.5 text-[14px] font-medium text-[#343139]"
      >
        {current.short}
        <span aria-hidden className="text-[9px] opacity-70">
          ▾
        </span>
      </button>
      {open && (
        <>
          <button
            type="button"
            aria-hidden
            tabIndex={-1}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-40 cursor-default"
          />
          <ul
            role="listbox"
            aria-label={t("language")}
            className="absolute right-0 z-50 mt-2 flex min-w-[148px] flex-col rounded-2xl border border-[rgba(25,24,24,0.12)] bg-white p-1.5 shadow-[0_18px_48px_-24px_rgba(25,24,24,0.45)]"
          >
            {LOCALES.map((l) => {
              const isActive = l.value === locale;
              return (
                <li key={l.value}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={isActive}
                    onClick={() => {
                      if (!isActive) setLocale(l.value);
                      setOpen(false);
                      onSelect?.();
                    }}
                    className={`flex w-full items-center justify-between gap-4 rounded-xl px-3 py-2 text-left text-[14px] font-medium transition-colors ${
                      isActive
                        ? "bg-[#19180F] text-[#F7F7F2]"
                        : "text-[#343139] hover:bg-[rgba(25,24,24,0.05)]"
                    }`}
                  >
                    {l.name}
                    <span className="text-[12px] opacity-60">{l.short}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}
